import { createContext, useCallback, useMemo, useRef, type PropsWithChildren } from 'react';

interface ShoppingCartContextType {
  triggerRefetch: () => void;
  subscribe: (callback: () => void) => () => void;
}

const ShoppingCartContext = createContext<ShoppingCartContextType>({
  triggerRefetch: () => {},
  subscribe: () => () => {},
});

const ShoppingCartProvider = ({ children }: PropsWithChildren) => {
  const subscribersRef = useRef<Set<() => void>>(new Set());

  const triggerRefetch = useCallback(() => {
    subscribersRef.current.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[ShoppingCartProvider] Error in subscriber callback:', error);
      }
    });
  }, []);

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const providerValue = useMemo(
    () => ({
      triggerRefetch,
      subscribe,
    }),
    [subscribe, triggerRefetch]
  );

  return <ShoppingCartContext.Provider value={providerValue}>{children}</ShoppingCartContext.Provider>;
};

ShoppingCartProvider.displayName = 'ShoppingCartProvider';

export { ShoppingCartProvider, ShoppingCartContext };
